import { queryKeys } from "@/constants/queryKeyFactory";
import { useMutation, useQuery } from "@/hooks/api";
import {
  ApiResponse,
  PaginationParams,
  QueryOptions,
} from "@/interfaces/api.interface";
import { ITaskAction, IRequestTasks } from "@/interfaces/task.interface";
import { queryClient } from "@/lib/queryClient";
import { tasksSvc } from "@/services/task.service";

export function useTasks(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IRequestTasks[]>>(
    queryKeys.tasks.list(params),
    () =>
      tasksSvc.get<IRequestTasks>({
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        sort: params.sort,
        filter: params.filter,
        searchFilters: params.searchFilters,
      }),
    options
  );
}

export function useAllTasks(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IRequestTasks[]>>(
    queryKeys.tasks.all({ ...params }),
    () => tasksSvc.getAll<IRequestTasks>(params),
    options
  );
}

export function useCreateTask(options: QueryOptions = {}) {
  return useMutation<ApiResponse<IRequestTasks>, IRequestTasks>(
    (data: IRequestTasks) => tasksSvc.create<IRequestTasks>(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.root,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.levels.root,
        });
      },
    }
  );
}

export function useUpdateTask(options: QueryOptions = {}) {
  return useMutation<
    ApiResponse<IRequestTasks>,
    { id: string; data: IRequestTasks }
  >(({ id, data }) => tasksSvc.update<IRequestTasks>(id, data), {
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.root,
        refetchType: "active",
      });
    },
  });
}

export function useTaskAction(options: QueryOptions = {}) {
  return useMutation<ApiResponse<ITaskAction>, ITaskAction>(
    (data) => tasksSvc.taskAction(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.root,
          refetchType: "active",
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.request.root,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.levels.root,
        });
      },
    }
  );
}

export function useTasksByRequest(
  requestId: string | null,
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ITaskAction[]>>(
    queryKeys.tasks.request(requestId || ""),
    () => tasksSvc.fetchTasksByRequestId(requestId!),
    options
  );
}
